import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, tap, catchError, takeUntil, take } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { NotificationSettings, GetUserBuildingGQL } from 'src/graphql/generated/schema';
import { UserSettingsService } from 'src/graphql/services/user-settings.service';
import { DynamoDBService } from 'src/app/services/dynamodb.service';
import { Storage } from '@ionic/storage-angular';

interface BuildingInfo {
  buildingId?: string;
  buildingName?: string;
  buildingAddress?: string;
  buildingCode?: string;
  floor?: string;
  unitName?: string;
  unitNumber?: string;
  bedrooms?: number;
  bathrooms?: number;
  propertyManagerEmail?: string;
  propertyManagerPhone?: string;
}

interface ProfilePageData {
  userAttributes: CognitoUserAttribute[] | null;
  buildingInfo: BuildingInfo | null;
}

// Bump whenever the shape of any cached settings blob changes so stale blobs from a
// prior app build are discarded on first launch instead of being trusted forever.
// v2: BuildingInfo gained buildingCode (My Vessel Guide link gates on it); pre-v2 caches
// lack the field and would keep the guide row hidden after an in-place upgrade.
// v3: cache blobs are now stamped with the Cognito sub that owns them (OWNER_SUB).
// Pre-v3 blobs carry no owner, so they can't be proven to belong to whoever is
// signed in now — the version bump discards them once on upgrade. That also
// repairs installs already showing another account's building without needing a
// sign-out.
const SETTINGS_CACHE_VERSION = 3;

// localStorage keys for synchronous reads
const LS_KEYS = {
  USER_ATTRIBUTES: 'settings_cache_user_attributes',
  BUILDING_INFO: 'settings_cache_building_info',
  NOTIFICATION_SETTINGS: 'settings_cache_notification_settings',
  PIN: 'settings_cache_pin',
  CACHE_VERSION: 'settings_cache_version',
  // Cognito sub these cached blobs describe. None of the payload keys are
  // user-scoped, so without this a blob written by one account is read straight
  // back by the next account on the same device.
  OWNER_SUB: 'settings_cache_owner_sub'
};

@Injectable({
  providedIn: 'root'
})
export class SettingsPreloadService {
  private userAttributesCache$ = new BehaviorSubject<CognitoUserAttribute[] | null>(null);
  private notificationSettingsCache$ = new BehaviorSubject<NotificationSettings | null>(null);
  private buildingInfoCache$ = new BehaviorSubject<BuildingInfo | null>(null);
  private pinCache$ = new BehaviorSubject<string | null>(null);
  private storage: Storage | null = null;

  private userAttributesLoading = false;
  private notificationSettingsLoading = false;
  private buildingInfoLoading = false;
  private pinLoading = false;

  // Set when the persisted cache is from an older shape version; drives a one-time purge
  // of the durable Ionic Storage cache in loadCachedData so stale blobs aren't reloaded.
  private cacheVersionChanged = false;

  private destroy$ = new Subject<void>();

  private readonly CACHE_KEYS = {
    USER_ATTRIBUTES: 'cached_user_attributes',
    BUILDING_INFO: 'cached_building_info',
    NOTIFICATION_SETTINGS: 'cached_notification_settings'
  };

  constructor(
    private authService: AuthService,
    private userSettingsService: UserSettingsService,
    private getUserBuildingGQL: GetUserBuildingGQL,
    private dynamoDBService: DynamoDBService,
    private storageService: Storage
  ) {
    // Synchronously load from localStorage so BehaviorSubjects have data immediately
    this.loadFromLocalStorage();
    // Also init Ionic Storage for durable persistence
    this.initStorage();
  }

  /**
   * Synchronously load cached data from localStorage.
   * This ensures BehaviorSubjects emit cached values immediately
   * before any async operations complete.
   */
  private loadFromLocalStorage(): void {
    // Discard caches written by an older shape version (e.g. pre-buildingCode BuildingInfo)
    // so they are refetched rather than trusted. Older builds never wrote a version, so a
    // missing/mismatched stamp counts as stale.
    try {
      if (localStorage.getItem(LS_KEYS.CACHE_VERSION) !== String(SETTINGS_CACHE_VERSION)) {
        this.cacheVersionChanged = true;
        [LS_KEYS.USER_ATTRIBUTES, LS_KEYS.BUILDING_INFO, LS_KEYS.NOTIFICATION_SETTINGS, LS_KEYS.PIN]
          .forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
        localStorage.setItem(LS_KEYS.CACHE_VERSION, String(SETTINGS_CACHE_VERSION));
        return; // nothing valid to load this launch; preload will fetch fresh
      }
    } catch { /* ignore storage errors; fall through to normal load */ }

    try {
      const userAttrs = localStorage.getItem(LS_KEYS.USER_ATTRIBUTES);
      if (userAttrs) {
        this.userAttributesCache$.next(JSON.parse(userAttrs));
      }
    } catch { /* ignore parse errors */ }

    try {
      const buildingInfo = localStorage.getItem(LS_KEYS.BUILDING_INFO);
      if (buildingInfo) {
        this.buildingInfoCache$.next(JSON.parse(buildingInfo));
      }
    } catch { /* ignore parse errors */ }

    try {
      const notifSettings = localStorage.getItem(LS_KEYS.NOTIFICATION_SETTINGS);
      if (notifSettings) {
        this.notificationSettingsCache$.next(JSON.parse(notifSettings));
      }
    } catch { /* ignore parse errors */ }

    try {
      const pin = localStorage.getItem(LS_KEYS.PIN);
      if (pin) {
        this.pinCache$.next(pin);
      }
    } catch { /* ignore parse errors */ }
  }

  /**
   * Write data to both localStorage (sync) and Ionic Storage (durable).
   */
  private persistCache(lsKey: string, ionicKey: string, data: any): void {
    // Every blob is written through here, so this is the one place ownership
    // needs recording; it cannot drift from the payload it describes.
    this.stampOwner();

    // localStorage for synchronous reads
    try {
      localStorage.setItem(lsKey, JSON.stringify(data));
    } catch { /* ignore quota errors */ }

    // Ionic Storage for durable persistence
    if (this.storage) {
      this.storage.set(ionicKey, data);
    }
  }

  async initStorage() {
    this.storage = await this.storageService.create();
    await this.loadCachedData();
  }

  private async loadCachedData(): Promise<void> {
    if (!this.storage) return;

    // loadFromLocalStorage already purged the sync cache for a shape-version change; clear
    // the durable Ionic mirror too so stale blobs aren't reloaded here. Preloads then fetch
    // fresh and repopulate both stores.
    if (this.cacheVersionChanged) {
      await Promise.all(
        Object.values(this.CACHE_KEYS).map(k => this.storage!.remove(k).catch(() => {}))
      );
      return;
    }

    // Only load from Ionic Storage if localStorage didn't have the data
    // (localStorage is the primary sync source; Ionic Storage is the fallback)
    if (this.userAttributesCache$.value === null) {
      const cachedUserAttributes = await this.storage.get(this.CACHE_KEYS.USER_ATTRIBUTES);
      if (cachedUserAttributes) {
        this.userAttributesCache$.next(cachedUserAttributes);
        // Backfill localStorage
        try { localStorage.setItem(LS_KEYS.USER_ATTRIBUTES, JSON.stringify(cachedUserAttributes)); } catch {}
      }
    }

    if (this.buildingInfoCache$.value === null) {
      const cachedBuildingInfo = await this.storage.get(this.CACHE_KEYS.BUILDING_INFO);
      if (cachedBuildingInfo) {
        this.buildingInfoCache$.next(cachedBuildingInfo);
        try { localStorage.setItem(LS_KEYS.BUILDING_INFO, JSON.stringify(cachedBuildingInfo)); } catch {}
      }
    }

    if (this.notificationSettingsCache$.value === null) {
      const cachedNotificationSettings = await this.storage.get(this.CACHE_KEYS.NOTIFICATION_SETTINGS);
      if (cachedNotificationSettings) {
        this.notificationSettingsCache$.next(cachedNotificationSettings);
        try { localStorage.setItem(LS_KEYS.NOTIFICATION_SETTINGS, JSON.stringify(cachedNotificationSettings)); } catch {}
      }
    }
  }

  /**
   * Discard the cache when it belongs to a different Cognito sub than the one
   * signed in now. Returns true when it purged.
   *
   * getCachedCognitoUserId reads the sub off the live ID token and falls back to
   * the stored sub. Its "telemetry only, not an auth gate" caveat doesn't bite
   * here: this decides which account a cached blob describes, it grants nothing.
   * A null sub means we can't tell yet, so we leave the cache alone rather than
   * blanking the UI on a cold-start race — the revalidate below still corrects
   * the contents, and the owner stamp lands with the next successful fetch.
   */
  private purgeIfDifferentUser(): boolean {
    let owner: string | null = null;
    try {
      owner = localStorage.getItem(LS_KEYS.OWNER_SUB);
    } catch { return false; }

    const current = this.authService.getCachedCognitoUserId();
    if (!current || !owner || owner === current) return false;

    void this.clearCache();
    return true;
  }

  /** Record which account the currently cached blobs describe. */
  private stampOwner(): void {
    const current = this.authService.getCachedCognitoUserId();
    if (!current) return;
    try { localStorage.setItem(LS_KEYS.OWNER_SUB, current); } catch { /* ignore quota errors */ }
  }

  /**
   * Preload all settings data.
   *
   * Stale-while-revalidate: cached values keep emitting immediately for instant
   * paint, and a fetch always runs behind them so the screen corrects itself
   * without a sign-out or a manual refresh. The old forceRefresh flag is gone —
   * every call revalidates now, and no caller ever passed it.
   */
  preloadAllSettingsData(): void {
    this.purgeIfDifferentUser();
    this.preloadUserAttributes();
    this.preloadNotificationSettings();
    this.preloadBuildingInfo();
    this.preloadPin();
  }

  private preloadUserAttributes(): void {
    if (this.userAttributesLoading) return;

    this.userAttributesLoading = true;
    this.authService.getUserAttributes(
      (err: any) => {
        console.error('Error preloading user attributes:', err);
        this.userAttributesLoading = false;
      },
      (res: CognitoUserAttribute[]) => {
        this.userAttributesCache$.next(res);
        this.userAttributesLoading = false;
        this.persistCache(LS_KEYS.USER_ATTRIBUTES, this.CACHE_KEYS.USER_ATTRIBUTES, res);
      }
    );
  }

  private preloadNotificationSettings(): void {
    if (this.notificationSettingsLoading) return;

    this.notificationSettingsLoading = true;
    this.userSettingsService.getUserNotificationSettings()
      .pipe(
        takeUntil(this.destroy$),
        tap(settings => {
          this.notificationSettingsCache$.next(settings);
          this.notificationSettingsLoading = false;
          this.persistCache(LS_KEYS.NOTIFICATION_SETTINGS, this.CACHE_KEYS.NOTIFICATION_SETTINGS, settings);
        }),
        catchError(err => {
          console.error('Error preloading notification settings:', err);
          this.notificationSettingsLoading = false;
          return of(null);
        })
      )
      .subscribe();
  }

  private preloadBuildingInfo(): void {
    if (this.buildingInfoLoading) return;

    this.buildingInfoLoading = true;
    this.getUserBuildingGQL.fetch()
      .pipe(
        takeUntil(this.destroy$),
        map((result: any) => {
          const building = result.data?.getUserBuilding;
          if (!building) return null;
          return {
            buildingId: building.id,
            buildingName: building.name,
            buildingAddress: building.address,
            buildingCode: building.buildingCode,
            floor: building.unit?.floor,
            unitName: building.unit?.name,
            unitNumber: building.unit?.unitNumber,
            bedrooms: building.unit?.bedrooms,
            bathrooms: building.unit?.bathrooms,
            propertyManagerEmail: building.propertyManagerEmail,
            propertyManagerPhone: building.propertyManagerPhone
          } as BuildingInfo;
        }),
        tap(buildingInfo => {
          this.buildingInfoCache$.next(buildingInfo);
          this.buildingInfoLoading = false;
          this.persistCache(LS_KEYS.BUILDING_INFO, this.CACHE_KEYS.BUILDING_INFO, buildingInfo);
        }),
        catchError(err => {
          console.error('Error preloading building info:', err);
          this.buildingInfoLoading = false;
          return of(null);
        })
      )
      .subscribe();
  }

  private preloadPin(): void {
    if (this.pinLoading) return;

    this.pinLoading = true;
    const cognitoId = this.authService.getCognitoUserId();
    if (!cognitoId) {
      this.pinLoading = false;
      return;
    }

    this.dynamoDBService.getResident(cognitoId)
      .then(resident => {
        if (resident?.['pin']?.S) {
          this.updatePinCache(resident['pin'].S);
        }
        this.pinLoading = false;
      })
      .catch(err => {
        console.error('Error preloading PIN:', err);
        this.pinLoading = false;
      });
  }

  /**
   * Update the cached PIN value (call after PIN changes).
   */
  updatePinCache(pin: string): void {
    this.pinCache$.next(pin);
    try { localStorage.setItem(LS_KEYS.PIN, pin); } catch {}
  }

  /**
   * Get cached PIN synchronously (for instant display).
   */
  getPinSync(): string | null {
    return this.pinCache$.value;
  }

  /**
   * Get cached building info synchronously (for instant display).
   */
  getBuildingInfoSync(): BuildingInfo | null {
    return this.buildingInfoCache$.value;
  }

  getUserAttributes(): Observable<CognitoUserAttribute[] | null> {
    // If we have cached data, return it immediately
    if (this.userAttributesCache$.value !== null) {
      return of(this.userAttributesCache$.value);
    }

    // Otherwise, load it and return the observable
    return new Observable(observer => {
      this.authService.getUserAttributes(
        (err: any) => {
          console.error('Error loading user attributes:', err);
          observer.next(null);
          observer.complete();
        },
        (res: CognitoUserAttribute[]) => {
          this.userAttributesCache$.next(res);
          this.persistCache(LS_KEYS.USER_ATTRIBUTES, this.CACHE_KEYS.USER_ATTRIBUTES, res);
          observer.next(res);
          observer.complete();
        }
      );
    });
  }

  getNotificationSettings(): Observable<NotificationSettings | null> {
    // This drives the route resolver, which the page reads via a one-time
    // activatedRoute.snapshot read (not a live subscription) - so unlike
    // getBuildingInfo()'s stale-while-revalidate (cache first, correct later),
    // returning a cached value here would let a resident's own toggle change
    // never show up on revisiting the tab without a full page reload. Always
    // fetch fresh; still update the cache as a side effect.
    return this.userSettingsService.getUserNotificationSettings()
      .pipe(
        tap(settings => {
          this.notificationSettingsCache$.next(settings);
          this.persistCache(LS_KEYS.NOTIFICATION_SETTINGS, this.CACHE_KEYS.NOTIFICATION_SETTINGS, settings);
        }),
        catchError(err => {
          console.error('Error loading notification settings:', err);
          return of(null);
        })
      );
  }

  getBuildingInfo(): Observable<BuildingInfo | null> {
    // Stale-while-revalidate. The cached value (if any) emits synchronously so
    // the row paints instantly, then the background fetch replaces it in place
    // and every async-pipe binding updates itself.
    //
    // This used to return of(cachedValue) and never refetch, so a resident who
    // changed unit — or a device whose previous account was deleted server-side —
    // kept seeing the old building and the old My Vessel Guide link until a full
    // sign-out cleared the cache.
    this.purgeIfDifferentUser();
    this.preloadBuildingInfo();
    return this.buildingInfoCache$.asObservable();
  }

  getProfilePageData(): Observable<ProfilePageData> {
    // Combine all data needed for profile page
    return new Observable(observer => {
      let userAttributes: CognitoUserAttribute[] | null = null;
      let buildingInfo: BuildingInfo | null = null;
      let completed = 0;

      const checkComplete = () => {
        completed++;
        if (completed === 2) {
          observer.next({ userAttributes, buildingInfo });
          observer.complete();
        }
      };

      // Get user attributes
      this.getUserAttributes().subscribe(attrs => {
        userAttributes = attrs;
        checkComplete();
      });

      // getBuildingInfo is a live stream now; this consumer wants a single
      // snapshot, so take the first emission and let the subscription close.
      this.getBuildingInfo().pipe(take(1)).subscribe(info => {
        buildingInfo = info;
        checkComplete();
      });
    });
  }

  async clearCache(): Promise<void> {
    this.userAttributesCache$.next(null);
    this.notificationSettingsCache$.next(null);
    this.buildingInfoCache$.next(null);
    this.pinCache$.next(null);

    // Clear localStorage
    try {
      localStorage.removeItem(LS_KEYS.USER_ATTRIBUTES);
      localStorage.removeItem(LS_KEYS.BUILDING_INFO);
      localStorage.removeItem(LS_KEYS.NOTIFICATION_SETTINGS);
      localStorage.removeItem(LS_KEYS.PIN);
      localStorage.removeItem(LS_KEYS.OWNER_SUB);
    } catch {}

    // Clear Ionic Storage
    if (this.storage) {
      await this.storage.remove(this.CACHE_KEYS.USER_ATTRIBUTES);
      await this.storage.remove(this.CACHE_KEYS.BUILDING_INFO);
      await this.storage.remove(this.CACHE_KEYS.NOTIFICATION_SETTINGS);
    }
  }

  cancelPreloading(): void {
    this.destroy$.next(undefined);
    this.userAttributesLoading = false;
    this.notificationSettingsLoading = false;
    this.buildingInfoLoading = false;
    this.pinLoading = false;
  }
}
